// ===================== Common Display Constants =====================
export const HYPHEN = '-'

// Display text used when an Internal/Global benchmark target has not been configured.
export const BENCHMARK_TARGET_NOT_CONFIGURED = 'Not Configured'

// Default locale used for number formatting across the application.
export const DEFAULT_LOCALE = 'en-US'

// Intl.NumberFormat options used to render compact ticks (e.g. 1.5K, 2.3M).
export const COMPACT_NUMBER_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  notation: 'compact',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}

// ===================== External URLs =====================
export const ADO_BOARD_BASE_URL =
  'https://dev.azure.com/PepsiCoIT/Global_Data_Project/_queries/query-edit/?newQuery=true'

// ===================== User Role Constants =====================
export const USER_ROLES = {
  ROLE_USER: 'ROLE_USER',
  ROLE_SPECIAL: 'ROLE_SPECIAL',
  ROLE_AI: 'ROLE_AI',
  ROLE_ADMIN: 'ROLE_ADMIN',
  ROLE_DNAI: 'ROLE_DNAI',
} as const

// ===================== Color Palettes =====================
export const colorPalette = ['#33ABDA', '#B05788', '#0078A7', '#8458CC', '#FF891C', '#818181']
export const colorPaletteForHorizontalGraphs = ['#33ABDA', '#0078A7', '#B05788', '#FF891C', '#8458CC', '#818181']
// ===================== Widget Titles =====================
export const WIDGET_TITLES = {
  LATEST_BUILDS: 'Latest Builds',
  COMMITMENT_MET_TREND: 'Commitment Met Trend',
  COMMITMENT_MET: 'Commitment Met',
  DEFECT_DENSITY: 'Defect Density',
  DEFECT_DENSITY_TREND: 'Defect Density Trend',
  DEFECT_DENSITY_BY_TEAM: 'Defect Density by Team Members',
  TEST_AUTOMATION_EFFICIENCY_TREND: 'Test Automation and Automation Efficiency Trends',
  BUG_TRENDS: 'Bug Trends',
  OPEN_BUGS_BY_SEVERITY: 'Open Bugs by Severity',
  AUTOMATION_EFFICIENCY: 'Automation Efficiency',
  AUTOMATION_EFFICIENCY_TREND: 'Automation Efficiency Trend',
  AUTOMATION_TEST_SUMMARY: 'Automation Test Summary',
  MANUAL_TEST_SUMMARY: 'Manual Test Summary',
  THRESHOLD_AUTOMATION_EFFICIENCY: 'Threshold Automation Efficiency',
  MANUAL_TEST_PLAN_TREND: 'Manual Test Plan Trend',
  BUGS_BY_TEAM: 'Bugs by Team',
  TEST_AUTOMATION: 'Test Automation',
  TEST_AUTOMATION_TREND: 'Test Automation Trend',
  BUILD_METRICS: 'Build Metrics & Breakdown',
  DEPLOY_METRICS: 'Deploy Metrics & Breakdown',
  VELOCITY: 'Velocity Story Points',
  OVERALL_AUTOMATION_COVERAGE: 'Overall Automation Coverage',
  OVERALL_AUTOMATION_COVERAGE_TREND: 'Overall Automation Coverage Trend',
  THRESHOLD_OVERALL_AUTOMATION_COVERAGE: 'Threshold Overall Automation Coverage',
} as const

// ===================== Benchmark Target Constants =====================
export const BENCHMARK_TARGET_LABELS = {
  GLOBAL: 'Global Target',
  INTERNAL: 'Internal Target',
} as const

export type BenchmarkTargetLabel = (typeof BENCHMARK_TARGET_LABELS)[keyof typeof BENCHMARK_TARGET_LABELS]

// Sentence-case variants rendered next to the value in BenchmarkTargetsCompact
export const BENCHMARK_TARGET_VALUE_LABELS = {
  GLOBAL: 'Global target',
  INTERNAL: 'Internal target',
} as const

export const BENCHMARK_GAP_TOOLTIP_PREFIX = 'Gap from'

// Chart series names for benchmark lines. These double as ECharts series identities —
// the tooltip handlers match on them, so the name and the lookup must come from here.
export const BENCHMARK_SERIES_LABELS = {
  GLOBAL: 'Global Benchmark',
  INTERNAL: 'Internal Benchmark',
} as const

// BENCHMARK_SERIES_COLORS lives in '@/theme/Color' alongside the palette it is built from.
// This module is intentionally dependency-free — it is imported almost everywhere, so importing
// the theme here would make any partial mock of the palette break unrelated suites at import time.

// How a benchmark progress bar derives its fill from the metric's current value.
// VALUE: the value is already a percentage, so the bar fills to it directly.
// TARGET: the value is a raw count (commits, PRs), so the bar fills to currentValue / target.
export const BENCHMARK_FILL_BASIS = {
  VALUE: 'value',
  TARGET: 'target',
} as const

export type BenchmarkFillBasis = (typeof BENCHMARK_FILL_BASIS)[keyof typeof BENCHMARK_FILL_BASIS]

// ===================== Chart/Widget Constants =====================
export const TARGET_DEFECT_DENSITY = 0.08
export const VISIBLE_SPRINTS_DATA_LENGTH = 6
export const STORIES_COMPLETED_TODAY_LABEL = 'Stories completed as of today'
export const CURRENT_SPRINT_LABEL = 'Current Sprint'
export const PREVIOUS_SPRINT_LABEL = 'Previous Sprint'
export const DEVIATION_FROM_GOAL_PREFIX = 'Deviation from configured goal of'
export const BUILDS_PER_DAY_TIME_RANGE = 14 // matching Angular implementation
export const DEFAULT_BUILD_TIME_THRESHOLD = 180000 // 3 minutes in milliseconds
export const DEPLOYMENT_DAYS_TO_FETCH = 14
export const BUILD_DAYS_TO_FETCH = 14
export const MAX_DEPLOYMENTS_TO_DISPLAY = 10
export const DEFAULT_ENVIRONMENT_VALUE = '_default'
export const NO_DEPLOYMENTS_TEXT = 'No Deployments'
export const BUILD_TABLE_MAX_PAGE_SIZE = 9999
export const SIDE_PANEL_HEIGHT = '85%'
export const COVERAGE_LABEL = 'Coverage'
export const TEST_AUTOMATION_THRESHOLD = 50
export const AUTOMATION_EFFICIENCY_THRESHOLD = 50
export const COMMITMENT_MET_THRESHOLD = 80
export const ON_TIME_DELIVERY_THRESHOLD = 80
export const ONE_DAY = '1 Day'
export const TWO_THREE_DAYS = '2-3 Days'
export const FOUR_FIVE_DAYS = '4-5 Days'
export const FIVE_PLUS_DAYS = '5+ Days'
export const LAST_N_DAYS_LABEL = (N: string | number) => `Last ${N} days`

// Iteration status order for charts and tables
export const ITERATION_STATUS_ORDER = [
  'New',
  'Analyzing',
  'Approved',
  'Backlog',
  'Committed',
  'In Progress',
  'Reviewing',
  'Validating',
  'Completed',
  'MVP',
  'Persevere',
  'Deploying',
  'Releasing',
  'Closed',
  'Rejected',
  'Removed',
]

// ===================== API/HTTP Constants =====================
export type HttpMethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
export const HTTPMethod: Record<'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH', HttpMethodType> = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
}
export const ContentType = {
  APPLICATION_JSON: 'application/json',
  MULTIPART_FORMDATA: 'multipart/form-data',
} as const
export const DEFAULT_CACHE_DURATION = 30 * 60 * 1000 // 30 minutes in milliseconds
export const OKTA_AUTH_CONFIG_KEY = 'okta-auth-config'
export const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage'

// HTTP Status Codes
export const HTTP_STATUS_CODES = {
  OK: 200,
  NO_CONTENT: 204,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const

// ===================== Build/Deployment Status Constants =====================
export const BUILD_STATUSES = {
  success: ['Success', 'succeeded', 'SUCCESS'],
  failure: ['Failed', 'Failure', 'FAILED', 'FAILURE', 'failed'],
  aborted: ['Aborted', 'ABORTED', 'abandoned'],
  notExecuted: ['NOT_EXECUTED', 'skipped'],
  inProgress: ['InProgress', 'in_progress', 'IN_PROGRESS', 'in progress'],
} as const
export const DEPLOYMENT_SUCCESS_STATUSES = ['succeeded', 'success']
export const ENVIRONMENTS = ['Prod', 'QA', 'UAT', 'SIT', 'Pre-PROD']

// ===================== UI Message & Table Config Constants =====================
export const MESSAGES = {
  NO_COMPONENT_ID: 'No component ID provided',
  NO_DEPLOYMENT_DATA: 'No deployment data available',
  NO_BUILD_DATA: 'No build data available',
  NO_BUILDS_FOR_PIPELINE: (pipeline: string) => `No builds found for pipeline "${pipeline}"`,
  NO_BUILD_DETAILS: 'No build details available',
  NO_DEPLOYMENT_DAYS: `No Data Found in last ${DEPLOYMENT_DAYS_TO_FETCH} days`,
  DELIVERY_BY_TEAM_TITLE: 'Delivery by Team',
  ON_TIME_DELIVERY_THRESHOLD_PREFIX: 'On-time delivery threshold is',
  NO_CODE_QUALITY_DATA: 'No Code Quality Data Available',
  NO_DATA_FOUND: 'No data found',
}
export const BUILD_STAGE_MESSAGES = {
  NO_BUILD_SELECTED: 'No build selected',
  NO_STAGES_FOUND: 'No stages found',
  NO_STAGES_WITH_STATUS: (status: string) => `No stages with status '${status}'`,
  UNKNOWN_STATUS: 'unknown',
}
export const BUILD_TABLE_CONFIG = {
  PAGE_SIZE: 10,
}

// DataTable column size defaults
export const DEFAULT_INITIAL_SIZE = 180
export const DEFAULT_MIN_SIZE = 150
export const DEFAULT_MAX_SIZE = 250

// ===================== Static Code Analysis Constants =====================
export const STATIC_ANALYSIS_METRICS = [
  {key: 'bugs', label: 'Bugs'},
  {key: 'alert_status', label: 'Quality Gate'},
  {key: 'sqale_index', label: 'Technical Debt'},
  {key: 'security_hotspots', label: 'Security Hotspots'},
  {key: 'security_rating', label: 'Security Rating'},
  {key: 'security_remediation_effort', label: 'Security Remediation Effort'},
  {key: 'duplicated_lines', label: 'Duplicate Lines'},
  {key: 'duplicated_blocks', label: 'Duplicate Blocks'},
] as const

export const VULNERABILITY_RATINGS: Record<string, string> = {
  '1.0': 'A',
  '2.0': 'B',
  '3.0': 'C',
  '4.0': 'D',
  '5.0': 'E',
}

export const SEVERITY_ORDER = ['1 - Critical', '2 - High', '3 - Medium', '4 - Low']
export const SEVERITY_LABELS = ['Critical', 'High', 'Medium', 'Low']

export const getLastNDaysLabel = (n: number) => `Last ${n} days`

// ===================== Date/Time Constants =====================
export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

// ===================== Defect Ageing Constants =====================
export const DEFECT_AGEING_PANEL_ID = 'defectAgeing'
export const DEFECT_AGEING_TITLE = 'Defect Ageing'
export const DEFECT_AGEING_DESCRIPTION = 'The age of the bug since the time they are opened.'
export const DEFECT_DETAILS_TITLE = 'Defect Details'
export const UNKNOWN_PROGRAM = 'Unknown Program'
export const SIDE_PANEL_HEIGHT_DEFECT_AGEING = '72%'

// Severity level constants
export const SEVERITY_LEVELS = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
} as const

// Severity pattern matching
export const SEVERITY_PATTERNS = {
  CRITICAL: ['1 - Critical', 'Critical'],
  HIGH: ['2 - High', 'High'],
  MEDIUM: ['3 - Medium', 'Medium'],
  LOW: ['4 - Low', 'Low'],
} as const

// Defect Ageing Side Panel Constants
export const UNKNOWN_SEVERITY = 'Unknown Severity'
export const UNKNOWN_TEAM = 'Unknown Team'
export const UNKNOWN_STATUS = 'Unknown'
export const UNKNOWN_BUG = 'Unknown Bug'

// Valid severity levels for validation
export const VALID_SEVERITY_LEVELS = ['Critical', 'High', 'Medium', 'Low', '1', '2', '3', '4']

// Defect age category names
export const DEFECT_AGE_CATEGORIES = ['1 Day', '2-3 Days', '4-5 Days', '5+ Days']

// Defect type categories
export const DEFECT_TYPES = ['Code Fix', 'Data', 'Config', 'UI', 'Design', 'caf']

// Tooltip definitions for widgets
export const TOOLTIP_DEFINITIONS = {
  TEST_AUTOMATION: 'Number of Test cases automated out of total Test cases identified',
  AUTOMATION_EFFICIENCY: 'Number of Defect found via automation against total number of defects',
  DEFECT_AGEING: 'Defect Age is defined as the age of the bug since the time they are opened',
  SONAR_CODE_SUMMARY:
    "The KPI's are aggregated from the latest snapshot of all the sonar projects configured in the program. Latest snapshot dates are",
  APPSEC_FLOW_SECURITY: 'Data based on AppSec Flow latest Snapshot:',
  COMMITMENT_MET:
    'Average Commitment met % of all teams in the program where commitment met is defined as the percentage of stories estimated on the first day of sprint versus user stories closed in the sprint',
  VELOCITY:
    'Average velocity of all teams in the program where velocity is defiend as the aggregated number of story points for all stories that are closed in the sprint',
  ON_TIME_DELIVERY: 'Percentage of stories in the program that are closed before the target date',
} as const
